import {
  Home as HomeIcon, BookOpen, GraduationCap, FileText, Newspaper,
  Phone, Briefcase, Globe,
} from 'lucide-react';

// ─── Page-content schema: 'theme-3' (EdTech Sales) ───────────────────────
//
// Theme-3 is a different layout, not a restyle, so it gets its own field set
// and its own page keys. Keys are prefixed `t3_` so an institute can hold
// theme-1 and theme-3 content side by side: switching themes must not destroy
// the other theme's copy, and `home.heroTitle` means something different in
// each (theme-3 splits the headline into a lead fragment and an italic
// emphasis fragment, which is a design element rather than formatting).
//
// Defaults live in code at vc_cms/themes/theme-3/content.js — every field here
// is an *override*, and leaving one blank falls back to that file. That is
// also why nothing here is required: a half-filled form must still render a
// complete page.
//
// Field reference: docs/cms/THEME3_CMS_FIELDS.md

// Sprite symbols in vc_cms/themes/theme-3/Icons.js, minus the UI chrome ones
// (chev-down, chev-right, menu, close, search). An icon name outside this list
// renders nothing at all — hence a select, never a free-text field.
const T3_ICONS = [
  'clock', 'monitor', 'users', 'briefcase', 'check', 'arrow-right', 'phone',
  'mail', 'pin', 'star', 'play', 'download', 'code', 'cloud', 'database',
  'spark', 'shield', 'calendar', 'layers', 'award', 'rupee', 'bell',
].map((v) => ({ value: v, label: v }));

const icon = (key = 'icon', label = 'Icon') => ({ key, label, type: 'icon', options: T3_ICONS });

/** `{question, answer}` list — the same shape theme-1 stores under `home.faqs`. */
const faqItems = (hint) => ({
  key: 'faqItems',
  label: 'Questions',
  type: 'repeater',
  hint,
  itemLabel: 'FAQ',
  titleKey: 'question',
  addLabel: 'Add question',
  defaultItem: { question: '', answer: '' },
  item: [
    { key: 'question', label: 'Question', type: 'text' },
    { key: 'answer', label: 'Answer', type: 'textarea', rows: 3 },
  ],
});

/** The mint closing band every list page ends on. */
const closingCta = (defaults = {}) => ([
  { key: 'ctaTitle', label: 'Closing CTA — title', type: 'text', placeholder: defaults.title },
  { key: 'ctaText', label: 'Closing CTA — text', type: 'textarea', rows: 2, placeholder: defaults.text },
  { key: 'ctaPrimary', label: 'Closing CTA — primary button', type: 'link', placeholder: defaults.primary, hrefPlaceholder: '/contact' },
  { key: 'ctaSecondary', label: 'Closing CTA — secondary button', type: 'link', placeholder: defaults.secondary, hrefPlaceholder: '/courses' },
]);

const emptyState = (defaults = {}) => ([
  { key: 'emptyTitle', label: 'Empty state — title', type: 'text', placeholder: defaults.title },
  { key: 'emptyText', label: 'Empty state — text', type: 'textarea', rows: 2, placeholder: defaults.text },
  { key: 'emptyCta', label: 'Empty state — button', type: 'text', placeholder: defaults.cta },
]);

/** One enquiry-modal variant. The modal swaps all of this by `variant`. */
const modalVariant = (v, label, d) => ({
  title: `Enquiry modal — ${label}`,
  fields: [
    { key: `modal_${v}_kicker`, label: 'Kicker', type: 'text', placeholder: d.kicker },
    { key: `modal_${v}_heading`, label: 'Heading', type: 'text', placeholder: d.heading },
    { key: `modal_${v}_subtitle`, label: 'Subtitle', type: 'textarea', rows: 2, placeholder: d.subtitle },
    { key: `modal_${v}_selectLabel`, label: 'Course field label', type: 'text', placeholder: d.selectLabel },
    { key: `modal_${v}_messageLabel`, label: 'Message field label', type: 'text', placeholder: d.messageLabel },
    { key: `modal_${v}_submitLabel`, label: 'Submit button', type: 'text', placeholder: d.submitLabel },
    { key: `modal_${v}_successTitle`, label: 'Success — title', type: 'text', placeholder: d.successTitle },
    { key: `modal_${v}_successText`, label: 'Success — body', type: 'textarea', rows: 2 },
  ],
});

const THEME3_SCHEMA = [
  // ══════════════════════════════════════════════════════════ GLOBAL / CHROME
  {
    pageKey: 't3_global',
    label: 'Global',
    icon: Globe,
    sections: [
      {
        title: 'Promo bar (home page only)',
        fields: [
          { key: 'promoEnabled', label: 'Show promo bar', type: 'toggle', default: true, onLabel: 'Shown', offLabel: 'Hidden' },
          { key: 'promoText', label: 'Promo text', type: 'text', placeholder: 'Next cohorts start 18 August · evening and weekend batches' },
          { key: 'promoLink', label: 'Promo link', type: 'link', placeholder: 'See the batch calendar →', hrefPlaceholder: '/courses' },
        ],
      },
      {
        title: 'Navigation',
        fields: [
          {
            key: 'navItems',
            label: 'Header navigation',
            type: 'repeater',
            hint: 'Leave empty to use the theme defaults (Courses, Placements, Jobs, About, Blogs, Contact). Links to Placements and Jobs are hidden automatically when those pages are switched off below.',
            itemLabel: 'Link',
            titleKey: 'label',
            addLabel: 'Add nav link',
            max: 7,
            inline: true,
            defaultItem: { label: '', href: '', enabled: true },
            item: [
              { key: 'label', label: 'Label', type: 'text' },
              { key: 'href', label: 'Link', type: 'text', placeholder: '/courses' },
              { key: 'enabled', label: 'Enabled', type: 'toggle', default: true, onLabel: 'Shown', offLabel: 'Hidden' },
            ],
          },
        ],
      },
      {
        title: 'Optional pages',
        fields: [
          {
            key: 'placementsEnabled',
            label: 'Placements page',
            type: 'toggle',
            default: false,
            onLabel: 'Live',
            offLabel: 'Off (404)',
            hint: 'Turn on once you have filled in the Placements tab. While off, /placements returns a clean 404 rather than showing sample content.',
          },
          {
            key: 'jobsEnabled',
            label: 'Jobs board',
            type: 'toggle',
            default: false,
            onLabel: 'Live',
            offLabel: 'Off (404)',
            hint: 'The job board has no data source yet — it is waiting on the Placement module. Leave this off until then.',
          },
        ],
      },
      {
        title: 'Footer',
        fields: [
          { key: 'footerBlurb', label: 'Footer blurb', type: 'textarea', rows: 2, placeholder: 'Career-focused technology training, taught by engineers who still ship code.' },
          {
            key: 'footerSocials',
            label: 'Social links',
            type: 'repeater',
            hint: 'Leave empty to reuse the profiles from SEO & Analytics → Social.',
            itemLabel: 'Social',
            titleKey: 'label',
            addLabel: 'Add social link',
            max: 6,
            inline: true,
            defaultItem: { label: '', text: '', href: '' },
            item: [
              { key: 'label', label: 'Name', type: 'text', placeholder: 'LinkedIn' },
              { key: 'text', label: 'Badge text', type: 'text', placeholder: 'in', maxLength: 3 },
              { key: 'href', label: 'URL', type: 'text', placeholder: 'https://…' },
            ],
          },
          {
            key: 'footerColumns',
            label: 'Footer link columns',
            type: 'repeater',
            hint: 'The design lays out exactly three columns.',
            itemLabel: 'Column',
            titleKey: 'title',
            addLabel: 'Add column',
            max: 3,
            defaultItem: { title: '', links: [] },
            item: [
              { key: 'title', label: 'Column heading', type: 'text', placeholder: 'Company' },
              {
                key: 'links',
                label: 'Links',
                type: 'repeater',
                itemLabel: 'Link',
                titleKey: 'label',
                addLabel: 'Add link',
                max: 6,
                inline: true,
                defaultItem: { label: '', href: '' },
                item: [
                  { key: 'label', label: 'Label', type: 'text' },
                  { key: 'href', label: 'Link', type: 'text', placeholder: '/courses' },
                ],
              },
            ],
          },
          { key: 'footerPhone', label: 'Contact phone', type: 'text', placeholder: 'Falls back to SEO → Organization phone' },
          { key: 'footerEmail', label: 'Contact email', type: 'text', placeholder: 'Falls back to SEO → Organization email' },
          { key: 'footerAddress1', label: 'Address line 1', type: 'text', placeholder: '2nd Floor, Tech Park Road,' },
          { key: 'footerAddress2', label: 'Address line 2', type: 'text', placeholder: 'Hyderabad 500081' },
          { key: 'footerCopyright', label: 'Copyright line', type: 'text', placeholder: '© 2026 Your Institute. All rights reserved.' },
          {
            key: 'footerLegal',
            label: 'Legal links',
            type: 'repeater',
            itemLabel: 'Link',
            titleKey: 'label',
            addLabel: 'Add legal link',
            max: 4,
            inline: true,
            defaultItem: { label: '', href: '' },
            item: [
              { key: 'label', label: 'Label', type: 'text', placeholder: 'Privacy Policy' },
              { key: 'href', label: 'Link', type: 'text', placeholder: '/privacy' },
            ],
          },
        ],
      },
      {
        title: 'Enquiry & callback forms — shared options',
        fields: [
          {
            key: 'formCourseOptions',
            label: 'Course dropdown options',
            type: 'repeater',
            hint: 'Used by the callback form, the contact form and all enquiry modals. Leave empty to list your published courses automatically.',
            itemLabel: 'Option',
            titleKey: 'label',
            addLabel: 'Add course option',
            max: 20,
            defaultItem: { label: '' },
            coerceRow: (row) => (typeof row === 'string' ? { label: row } : row),
            item: [{ key: 'label', label: 'Label', type: 'text' }],
          },
          {
            key: 'formModeOptions',
            label: 'Preferred-mode options',
            type: 'repeater',
            itemLabel: 'Option',
            titleKey: 'label',
            addLabel: 'Add mode',
            max: 6,
            defaultItem: { label: '' },
            coerceRow: (row) => (typeof row === 'string' ? { label: row } : row),
            item: [{ key: 'label', label: 'Label', type: 'text', placeholder: 'Live online' }],
          },
          {
            key: 'formTimeOptions',
            label: 'Best-time-to-call options',
            type: 'repeater',
            itemLabel: 'Option',
            titleKey: 'label',
            addLabel: 'Add time window',
            max: 6,
            defaultItem: { label: '' },
            coerceRow: (row) => (typeof row === 'string' ? { label: row } : row),
            item: [{ key: 'label', label: 'Label', type: 'text', placeholder: 'Morning · 9 am – 12 pm' }],
          },
          { key: 'formConsentText', label: 'Consent checkbox text', type: 'textarea', rows: 2, placeholder: 'I agree to be contacted about batches, demos and counselling on WhatsApp or phone.' },
        ],
      },
      modalVariant('demo', 'Book a demo', {
        kicker: 'Free demo class',
        heading: 'Book a free demo class',
        subtitle: 'Sit in on a live session with the trainer and the current cohort.',
        selectLabel: 'Course of interest',
        messageLabel: 'Anything we should know?',
        submitLabel: 'Book My Demo Slot',
        successTitle: 'Demo slot requested',
      }),
      modalVariant('counsellor', 'Talk to a counsellor', {
        kicker: 'Free counselling',
        heading: 'Talk to a career counsellor',
        subtitle: 'Tell us where you are today and a counsellor will map the shortest credible path.',
        selectLabel: 'Course of interest',
        messageLabel: 'Anything we should know?',
        submitLabel: 'Request a Callback',
        successTitle: 'Enquiry received',
      }),
      {
        ...modalVariant('apply', 'Job application', {
          kicker: 'Job application',
          heading: 'Apply through us',
          subtitle: 'Your application goes to the hiring manager with your project portfolio attached.',
          selectLabel: 'Programme you studied with us',
          messageLabel: 'Why this role?',
          submitLabel: 'Submit Application',
          successTitle: 'Application submitted',
        }),
        // Only reachable from the Jobs board.
        visibleWhen: { key: 'jobsEnabled', equals: true },
      },
    ],
  },

  // ══════════════════════════════════════════════════════════════════ HOME
  {
    pageKey: 't3_home',
    label: 'Home',
    icon: HomeIcon,
    sections: [
      {
        title: 'Hero',
        fields: [
          { key: 'heroKicker', label: 'Kicker', type: 'text', placeholder: 'Build skills. Build your career.' },
          {
            key: 'heroTitleLead',
            label: 'Headline — leading text',
            type: 'text',
            placeholder: 'Industry-focused technology training built for ',
            hint: 'End this with a space. The italic fragment below is joined onto it inline, so without the trailing space the two run together.',
          },
          { key: 'heroTitleEm', label: 'Headline — italic emphasis', type: 'text', placeholder: 'real careers.' },
          { key: 'heroLead', label: 'Sub-headline', type: 'textarea', rows: 3, placeholder: 'Live instructor-led programmes in full stack, cloud, data and AI…' },
          { key: 'heroPrimaryCta', label: 'Primary button', type: 'link', placeholder: 'Explore Courses', hrefPlaceholder: '/courses' },
          { key: 'heroSecondaryCta', label: 'Secondary button', type: 'link', placeholder: 'Talk to a Counsellor', hrefPlaceholder: '/contact' },
          {
            key: 'heroProof',
            label: 'Proof points',
            type: 'repeater',
            hint: 'The three ticked phrases under the buttons.',
            itemLabel: 'Point',
            titleKey: 'text',
            addLabel: 'Add proof point',
            max: 3,
            defaultItem: { text: '' },
            coerceRow: (row) => (typeof row === 'string' ? { text: row } : row),
            item: [{ key: 'text', label: 'Text', type: 'text', placeholder: 'Live instructor-led' }],
          },
          { key: 'heroImage', label: 'Hero image', type: 'image', hint: 'Shown in a 4:3 frame. Recommended 1200×900 px.' },
          { key: 'heroImageAlt', label: 'Hero image — alt text (SEO)', type: 'text', placeholder: 'e.g. Trainer reviewing code with students' },
          { key: 'heroFloatTitle', label: 'Floating badge — title', type: 'text', placeholder: 'Batches of 18, not 180' },
          { key: 'heroFloatSub', label: 'Floating badge — subtitle', type: 'text', placeholder: 'Every learner gets code review' },
        ],
      },
      {
        title: 'Stats band',
        fields: [
          { key: 'statsVisible', label: 'Show this section', type: 'toggle', default: true },
          {
            key: 'stats',
            label: 'Stat counters',
            type: 'repeater',
            hint: 'These animate up from zero, so start the value with a digit — "10,000+" counts, "Over 10,000" does not.',
            itemLabel: 'Stat',
            titleKey: 'label',
            addLabel: 'Add stat',
            max: 4,
            inline: true,
            defaultItem: { value: '', label: '' },
            item: [
              { key: 'value', label: 'Value', type: 'text', placeholder: '10,000+' },
              { key: 'label', label: 'Label', type: 'text', placeholder: 'Students trained' },
            ],
          },
        ],
      },
      {
        title: 'Technology marquee',
        fields: [
          { key: 'marqueeVisible', label: 'Show this section', type: 'toggle', default: true },
          { key: 'marqueeLabel', label: 'Band label', type: 'text', placeholder: 'Tools you ship with' },
          {
            key: 'marqueeItems',
            label: 'Tool names',
            type: 'repeater',
            hint: 'Six or more reads best — the strip scrolls continuously.',
            itemLabel: 'Tool',
            titleKey: 'text',
            addLabel: 'Add tool',
            max: 20,
            defaultItem: { text: '' },
            coerceRow: (row) => (typeof row === 'string' ? { text: row } : row),
            item: [{ key: 'text', label: 'Name', type: 'text', placeholder: 'React' }],
          },
        ],
      },
      {
        title: 'Courses section',
        fields: [
          { key: 'coursesVisible', label: 'Show this section', type: 'toggle', default: true },
          { key: 'coursesKicker', label: 'Kicker', type: 'text', placeholder: 'Popular programs' },
          { key: 'coursesHeading', label: 'Heading', type: 'text', maxLength: 90, placeholder: 'Learn the skills employers are actually hiring for.' },
          { key: 'coursesLead', label: 'Lead', type: 'textarea', rows: 2 },
          { key: 'coursesAllCta', label: '“All courses” link', type: 'link', placeholder: 'All courses', hrefPlaceholder: '/courses' },
          {
            key: 'coursesFilters',
            label: 'Filter pills',
            type: 'repeater',
            hint: 'Clicking a pill opens the Courses page filtered to that category.',
            itemLabel: 'Pill',
            titleKey: 'label',
            addLabel: 'Add filter pill',
            max: 6,
            inline: true,
            defaultItem: { label: '', icon: '', categoryId: '' },
            item: [
              { key: 'label', label: 'Label', type: 'text', placeholder: 'Cloud & DevOps' },
              icon(),
              { key: 'categoryId', label: 'Category id', type: 'text', placeholder: 'cloud' },
            ],
          },
          {
            key: 'coursesSource',
            label: 'Which courses to show',
            type: 'select',
            options: [
              { value: 'auto', label: 'Automatically — most recent published courses' },
              { value: 'picked', label: 'Hand-picked (below)' },
            ],
            emptyOption: 'Automatically — most recent published courses',
          },
          {
            key: 'coursesPicked',
            label: 'Hand-picked courses',
            type: 'repeater',
            hint: 'Only used when “Hand-picked” is selected above. Enter the course slug exactly as it appears in its public URL.',
            itemLabel: 'Course',
            titleKey: 'slug',
            addLabel: 'Add course',
            max: 3,
            defaultItem: { slug: '' },
            item: [{ key: 'slug', label: 'Course slug', type: 'text', placeholder: 'java-full-stack' }],
          },
          { key: 'coursesFeaturedSlug', label: 'Featured (dark) card — course slug', type: 'text', placeholder: 'generative-ai-for-engineers' },
          { key: 'coursesFeaturedCta', label: 'Featured card — button', type: 'link', placeholder: 'Book a Free Demo', hrefPlaceholder: '/contact' },
        ],
      },
      {
        title: 'Why choose us',
        fields: [
          { key: 'whyVisible', label: 'Show this section', type: 'toggle', default: true },
          { key: 'whyKicker', label: 'Kicker', type: 'text', placeholder: 'Why choose us' },
          { key: 'whyHeading', label: 'Heading', type: 'text', placeholder: 'More than courses. A complete career learning experience.' },
          { key: 'whyLead', label: 'Lead', type: 'textarea', rows: 2 },
          {
            key: 'whyFeatures',
            label: 'Feature cards',
            type: 'repeater',
            hint: 'Three or six read best — the grid is three across.',
            itemLabel: 'Feature',
            titleKey: 'title',
            addLabel: 'Add feature',
            max: 6,
            defaultItem: { icon: 'spark', title: '', text: '' },
            item: [
              icon(),
              { key: 'title', label: 'Title', type: 'text', placeholder: 'Live instructor-led classes' },
              { key: 'text', label: 'Description', type: 'textarea', rows: 2 },
            ],
          },
        ],
      },
      {
        // After "why us", before the placement stories. Off by default — it
        // was added after live sites were built on this layout.
        id: 'team',
        title: 'Trainers & mentors',
        hint: 'The people from your Team tab, on the home page. Skipped while nobody matches who you chose to show.',
        fields: [
          { key: 'teamVisible', label: 'Show this section', type: 'toggle', default: false },
          {
            key: 'teamShow',
            label: 'Who to show',
            type: 'select',
            emptyOption: 'Trainers and mentors',
            options: [
              { value: 'trainers', label: 'Trainers only' },
              { value: 'mentors', label: 'Mentors only' },
            ],
            hint: 'Someone marked “Trainer & mentor” counts as both.',
          },
          {
            key: 'teamLimit',
            label: 'How many',
            type: 'select',
            emptyOption: 'Up to 8',
            options: [
              { value: '4', label: 'Up to 4' },
              { value: '12', label: 'Up to 12' },
            ],
            hint: 'In your Team tab order. When there are more, the link below shows everyone on the About page.',
          },
          { key: 'teamKicker', label: 'Kicker', type: 'text', placeholder: 'Trainers & mentors' },
          {
            key: 'teamHeading',
            label: 'Heading',
            type: 'text',
            maxLength: 90,
            placeholder: 'The people who teach you, and the ones who guide you.',
            hint: 'Left blank, it follows who is shown — trainers, mentors or both.',
          },
          { key: 'teamLead', label: 'Lead', type: 'textarea', rows: 2 },
          { key: 'teamAllCta', label: 'Link to the About page', type: 'link', placeholder: 'Meet the whole team', hrefPlaceholder: '/about#team' },
        ],
      },
      {
        title: 'Placements teaser',
        fields: [
          { key: 'placementsVisible', label: 'Show this section', type: 'toggle', default: true },
          { key: 'placementsKicker', label: 'Kicker', type: 'text', placeholder: 'Career support' },
          { key: 'placementsHeading', label: 'Heading', type: 'text', maxLength: 90, placeholder: 'Support that starts before the course ends.' },
          { key: 'placementsLead', label: 'Lead', type: 'textarea', rows: 2 },
          {
            key: 'placementsTicks',
            label: 'Checklist',
            type: 'repeater',
            hint: 'The bold fragment is the label; the rest continues the sentence — start it with a spaced dash, e.g. “ — two projects taken to a standard you can defend.”',
            itemLabel: 'Item',
            titleKey: 'strong',
            addLabel: 'Add checklist item',
            max: 3,
            defaultItem: { strong: '', rest: '' },
            item: [
              { key: 'strong', label: 'Bold label', type: 'text', placeholder: 'Portfolio review' },
              { key: 'rest', label: 'Rest of the sentence', type: 'text', placeholder: ' — two projects taken to a standard you can defend.' },
            ],
          },
          { key: 'placementsPrimaryCta', label: 'Primary button', type: 'link', placeholder: 'Talk to a Counsellor', hrefPlaceholder: '/contact' },
          { key: 'placementsSecondaryCta', label: 'Secondary button', type: 'link', placeholder: 'Download Curriculum', hrefPlaceholder: '/contact' },
          {
            key: 'placementsQuoteSource',
            label: 'Where the two quotes come from',
            type: 'select',
            options: [
              { value: 'testimonials', label: 'My published testimonials' },
              { value: 'manual', label: 'Written below' },
            ],
            emptyOption: 'My published testimonials',
          },
          {
            key: 'placementsQuotes',
            label: 'Quotes',
            type: 'repeater',
            hint: 'Exactly two are shown, and the first renders on the dark panel. Only used when “Written below” is selected.',
            itemLabel: 'Quote',
            titleKey: 'name',
            addLabel: 'Add quote',
            max: 2,
            defaultItem: { text: '', name: '', role: '', from: '', to: '', kicker: '' },
            item: [
              { key: 'text', label: 'Quote', type: 'textarea', rows: 3 },
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'role', label: 'Role / batch', type: 'text', placeholder: 'DevOps Engineer · Cloud & DevOps, 2025 batch' },
              { key: 'kicker', label: 'Kicker (dark panel only)', type: 'text', placeholder: 'Success story' },
              { key: 'from', label: 'Career from', type: 'text', placeholder: 'Support' },
              { key: 'to', label: 'Career to', type: 'text', placeholder: 'DevOps' },
            ],
          },
        ],
      },
      {
        title: 'Blog section',
        fields: [
          { key: 'blogsVisible', label: 'Show this section', type: 'toggle', default: true },
          { key: 'blogsKicker', label: 'Kicker', type: 'text', placeholder: 'From the blog' },
          { key: 'blogsHeading', label: 'Heading', type: 'text', maxLength: 90, placeholder: 'Practical reading for your next interview.' },
          { key: 'blogsAllCta', label: '“All articles” link', type: 'link', placeholder: 'All articles', hrefPlaceholder: '/blogs' },
          { key: 'blogsMoreTitle', label: 'Side list heading', type: 'text', placeholder: 'Also worth reading' },
          {
            key: 'blogsSource',
            label: 'Which posts to show',
            type: 'select',
            options: [
              { value: 'latest', label: 'Automatically — most recent published posts' },
              { value: 'picked', label: 'Hand-picked (below)' },
            ],
            emptyOption: 'Automatically — most recent published posts',
          },
          {
            key: 'blogsFeatured',
            label: 'Featured posts (2 large cards)',
            type: 'repeater',
            itemLabel: 'Post',
            titleKey: 'slug',
            addLabel: 'Add featured post',
            max: 2,
            defaultItem: { slug: '' },
            item: [{ key: 'slug', label: 'Post slug', type: 'text', placeholder: 'spring-boot-interview-questions' }],
          },
          {
            key: 'blogsMore',
            label: 'Side list posts (3 rows)',
            type: 'repeater',
            itemLabel: 'Post',
            titleKey: 'slug',
            addLabel: 'Add side-list post',
            max: 3,
            defaultItem: { slug: '' },
            item: [{ key: 'slug', label: 'Post slug', type: 'text' }],
          },
        ],
      },
      {
        title: 'FAQ',
        fields: [
          { key: 'faqVisible', label: 'Show this section', type: 'toggle', default: true },
          { key: 'faqKicker', label: 'Kicker', type: 'text', placeholder: 'Common questions' },
          { key: 'faqHeading', label: 'Heading', type: 'text', maxLength: 80, placeholder: 'Everything students ask before enrolling.' },
          { key: 'faqLead', label: 'Lead', type: 'textarea', rows: 2 },
          { key: 'faqCta', label: 'Link', type: 'link', placeholder: 'Ask a counsellor', hrefPlaceholder: '/contact' },
          faqItems(),
        ],
      },
      {
        title: 'Closing CTA & callback form',
        fields: [
          { key: 'ctaHeading', label: 'Heading', type: 'text', placeholder: 'Ready to start your career journey?' },
          { key: 'ctaLead', label: 'Lead', type: 'textarea', rows: 2 },
          { key: 'ctaPhone', label: 'Phone', type: 'text', placeholder: 'Falls back to SEO → Organization phone' },
          { key: 'ctaEmail', label: 'Email', type: 'text', placeholder: 'Falls back to SEO → Organization email' },
          { key: 'ctaAddress', label: 'Address', type: 'text', placeholder: 'Tech Park Road, Hyderabad 500081' },
          { key: 'ctaFormTitle', label: 'Callback form — title', type: 'text', placeholder: 'Request a callback' },
          { key: 'ctaFormSub', label: 'Callback form — subtitle', type: 'text', placeholder: 'Four fields. We reply within one working day.' },
          { key: 'ctaFormSubmit', label: 'Callback form — submit button', type: 'text', placeholder: 'Request a callback' },
          { key: 'ctaFormSuccess', label: 'Callback form — success message', type: 'textarea', rows: 2, placeholder: 'Enquiry received — a counsellor will call you within one working day.' },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════ COURSES
  {
    pageKey: 't3_courses',
    label: 'Courses',
    icon: BookOpen,
    sections: [
      {
        title: 'Page header',
        fields: [
          { key: 'crumb', label: 'Breadcrumb label', type: 'text', placeholder: 'Courses' },
          { key: 'kicker', label: 'Kicker', type: 'text', placeholder: 'All programmes' },
          { key: 'title', label: 'Page title', type: 'text', placeholder: 'Career programmes in software, cloud and data.' },
          { key: 'lead', label: 'Lead', type: 'textarea', rows: 2 },
          {
            key: 'mini',
            label: 'Mini stat strip',
            type: 'repeater',
            itemLabel: 'Stat',
            titleKey: 'label',
            addLabel: 'Add stat',
            max: 4,
            inline: true,
            defaultItem: { num: '', label: '' },
            item: [
              { key: 'num', label: 'Value', type: 'text', placeholder: '12' },
              { key: 'label', label: 'Label', type: 'text', placeholder: 'Programmes' },
            ],
          },
        ],
      },
      {
        title: 'Filter bar',
        fields: [
          { key: 'searchPlaceholder', label: 'Search placeholder', type: 'text', placeholder: 'Search courses, skills or tools' },
          {
            key: 'dropdownLabels',
            label: 'Filter dropdown labels',
            type: 'repeater',
            itemLabel: 'Dropdown',
            titleKey: 'label',
            addLabel: 'Add dropdown',
            max: 3,
            defaultItem: { label: '' },
            coerceRow: (row) => (typeof row === 'string' ? { label: row } : row),
            item: [{ key: 'label', label: 'Label', type: 'text', placeholder: 'Mode' }],
          },
          { key: 'sortLabel', label: 'Sort control label', type: 'text', placeholder: 'Sort: Most popular' },
          {
            key: 'categories',
            label: 'Category pills',
            type: 'repeater',
            itemLabel: 'Category',
            titleKey: 'label',
            addLabel: 'Add category',
            max: 8,
            inline: true,
            defaultItem: { id: '', label: '', icon: '' },
            item: [
              { key: 'id', label: 'Id', type: 'text', placeholder: 'cloud' },
              { key: 'label', label: 'Label', type: 'text', placeholder: 'Cloud & DevOps' },
              icon(),
            ],
          },
          { key: 'priceNote', label: 'Price footnote', type: 'text', placeholder: 'Prices exclude GST · EMI available on all programmes' },
        ],
      },
      {
        title: 'Featured programme',
        fields: [
          { key: 'featuredVisible', label: 'Show this section', type: 'toggle', default: true },
          { key: 'featuredSlug', label: 'Course slug', type: 'text', placeholder: 'java-full-stack', hint: 'Exactly as it appears in the course’s public URL.' },
          { key: 'featuredKicker', label: 'Kicker', type: 'text', placeholder: 'Featured programme' },
          { key: 'featuredTitle', label: 'Title', type: 'text', placeholder: 'Java Full Stack — the flagship six-month track' },
          { key: 'featuredLead', label: 'Lead', type: 'textarea', rows: 3 },
          { key: 'featuredPrimaryCta', label: 'Primary button', type: 'link', placeholder: 'View Course' },
          { key: 'featuredSecondaryCta', label: 'Secondary button', type: 'link', placeholder: 'Book a Free Demo', hrefPlaceholder: '/contact' },
          {
            key: 'featuredCells',
            label: 'Fact cells',
            type: 'repeater',
            itemLabel: 'Cell',
            titleKey: 'label',
            addLabel: 'Add fact',
            max: 4,
            inline: true,
            defaultItem: { label: '', value: '' },
            item: [
              { key: 'label', label: 'Label', type: 'text', placeholder: 'Duration' },
              { key: 'value', label: 'Value', type: 'text', placeholder: '6 months' },
            ],
          },
        ],
      },
      {
        title: 'Empty state',
        fields: emptyState({
          title: 'No programmes match this filter yet',
          text: 'Tell a counsellor what you’re looking for.',
          cta: 'Clear filters',
        }),
      },
      {
        title: 'Closing CTA',
        fields: closingCta({
          title: 'Not sure which programme fits your experience?',
          text: 'Tell a counsellor where you are today.',
          primary: 'Talk to a Counsellor',
          secondary: 'Download Curriculum',
        }),
      },
    ],
  },

  // ═════════════════════════════════════════════════════════ COURSE DETAIL
  {
    pageKey: 't3_courseDetail',
    label: 'Course detail',
    icon: GraduationCap,
    intro:
      'These settings apply to every course page. Each course supplies its own title, description, '
      + 'learning outcomes, curriculum, prerequisites, batches, trainer, price, FAQs and skill chips '
      + '— edit those under Courses → the course → Public Website (CMS). Sections a course has no '
      + 'data for are hidden on that course rather than filled with sample content.',
    sections: [
      {
        title: 'Section headings',
        fields: [
          { key: 'overviewKicker', label: 'Overview — kicker', type: 'text', placeholder: 'Course overview' },
          { key: 'overviewTitle', label: 'Overview — heading', type: 'text', placeholder: 'What this programme covers' },
          { key: 'outcomesTitle', label: 'Outcomes — heading', type: 'text', placeholder: "What you'll be able to do" },
          { key: 'techTitle', label: 'Skills & technologies — heading', type: 'text', placeholder: 'Technologies covered', hint: 'The chips below it come from each course’s “Skills & Technologies” field.' },
          { key: 'curriculumKicker', label: 'Curriculum — kicker', type: 'text', placeholder: 'Curriculum' },
          { key: 'audienceNeedTitle', label: 'Prerequisites heading', type: 'text', placeholder: 'What you need before starting' },
          { key: 'audienceBatchTitle', label: 'Batches heading', type: 'text', placeholder: 'Batches and timings' },
          { key: 'instructorKicker', label: 'Instructor — kicker', type: 'text', placeholder: 'Your instructor' },
        ],
      },
      {
        title: 'Enrolment panel',
        fields: [
          { key: 'asidePriceSuffix', label: 'Price suffix', type: 'text', placeholder: '+ GST' },
          { key: 'asidePrimaryCta', label: 'Primary button', type: 'link', placeholder: 'Book a Free Demo', hrefPlaceholder: '/contact' },
          { key: 'asideSecondaryCta', label: 'Secondary button', type: 'link', placeholder: 'Talk to a Counsellor', hrefPlaceholder: '/contact' },
          {
            key: 'asideIncludes',
            label: '“What’s included” list',
            type: 'repeater',
            hint: 'Used on any course that has no Highlights of its own.',
            itemLabel: 'Item',
            titleKey: 'text',
            addLabel: 'Add item',
            max: 5,
            defaultItem: { text: '' },
            coerceRow: (row) => (typeof row === 'string' ? { text: row } : row),
            item: [{ key: 'text', label: 'Text', type: 'text', placeholder: 'Lifetime access to session recordings' }],
          },
        ],
      },
      {
        title: 'Placement block',
        fields: [
          { key: 'placementVisible', label: 'Show this section', type: 'toggle', default: true },
          { key: 'placementKicker', label: 'Kicker', type: 'text', placeholder: 'Placement support' },
          { key: 'placementTitle', label: 'Title', type: 'text', placeholder: 'Interview preparation starts in month four.' },
          { key: 'placementLead', label: 'Lead', type: 'textarea', rows: 2 },
          {
            key: 'placementTicks',
            label: 'Checklist',
            type: 'repeater',
            itemLabel: 'Item',
            titleKey: 'strong',
            addLabel: 'Add checklist item',
            max: 3,
            defaultItem: { strong: '', rest: '' },
            item: [
              { key: 'strong', label: 'Bold label', type: 'text' },
              { key: 'rest', label: 'Rest of the sentence', type: 'text' },
            ],
          },
          { key: 'placementCta', label: 'Button', type: 'link', placeholder: 'Talk to a Counsellor', hrefPlaceholder: '/contact' },
        ],
      },
      {
        title: 'Course FAQs',
        fields: [
          { key: 'faqKicker', label: 'Kicker', type: 'text', placeholder: 'Course FAQs' },
          { key: 'faqTitle', label: 'Title', type: 'text', placeholder: 'Questions we get before every batch.' },
          { key: 'faqLead', label: 'Lead', type: 'textarea', rows: 2 },
          { key: 'faqCta', label: 'Link', type: 'link', placeholder: 'Ask a counsellor', hrefPlaceholder: '/contact' },
          faqItems('Shown on any course that has no FAQs of its own.'),
        ],
      },
      {
        title: 'Closing CTA',
        fields: closingCta({
          title: 'The next batch starts soon.',
          text: 'Sit in on a live class first — no payment, no commitment.',
          primary: 'Book a Free Demo',
          secondary: 'Request a Callback',
        }),
      },
    ],
  },

  // ═════════════════════════════════════════════════════════════════ BLOGS
  {
    pageKey: 't3_blogs',
    label: 'Blogs',
    icon: FileText,
    sections: [
      {
        title: 'Page header',
        fields: [
          { key: 'crumb', label: 'Breadcrumb label', type: 'text', placeholder: 'Blogs' },
          { key: 'kicker', label: 'Kicker', type: 'text', placeholder: 'Writing' },
          { key: 'title', label: 'Page title', type: 'text', placeholder: 'Notes on building software and building a career.' },
          { key: 'lead', label: 'Lead', type: 'textarea', rows: 2 },
          {
            key: 'categories',
            label: 'Category pills',
            type: 'repeater',
            hint: 'Leave empty to build the list automatically from your posts’ tags.',
            itemLabel: 'Category',
            titleKey: 'label',
            addLabel: 'Add category',
            max: 8,
            inline: true,
            defaultItem: { id: '', label: '' },
            item: [
              { key: 'id', label: 'Tag', type: 'text', placeholder: 'career' },
              { key: 'label', label: 'Label', type: 'text', placeholder: 'Career' },
            ],
          },
        ],
      },
      {
        title: 'Featured post',
        fields: [
          {
            key: 'featuredMode',
            label: 'Which post is featured',
            type: 'select',
            options: [
              { value: 'latest', label: 'Most recent published post' },
              { value: 'picked', label: 'The post named below' },
            ],
            emptyOption: 'Most recent published post',
          },
          { key: 'featuredSlug', label: 'Post slug', type: 'text', placeholder: 'spring-boot-interview-questions' },
          { key: 'featuredFlag', label: 'Badge text', type: 'text', placeholder: 'Featured' },
          { key: 'featuredCta', label: 'Button', type: 'text', placeholder: 'Read the article' },
        ],
      },
      {
        title: 'Empty state',
        fields: emptyState({
          title: 'Nothing published in this category yet',
          text: 'We publish two or three articles a month. Try another category.',
          cta: 'Show all articles',
        }),
      },
      {
        title: 'Subscribe block',
        fields: [
          {
            key: 'subscribeEnabled',
            label: 'Show subscribe block',
            type: 'toggle',
            default: false,
            hint: 'Submissions arrive as enquiries tagged “Blog subscribe”. Leave this off if nobody is going to read them.',
          },
          { key: 'subscribeTitle', label: 'Title', type: 'text', placeholder: 'One article a fortnight. No sales emails.' },
          { key: 'subscribeText', label: 'Text', type: 'textarea', rows: 2 },
          { key: 'subscribePlaceholder', label: 'Input placeholder', type: 'text', placeholder: 'Email address' },
          { key: 'subscribeCta', label: 'Button', type: 'text', placeholder: 'Subscribe' },
          { key: 'subscribeSuccess', label: 'Success message', type: 'text', placeholder: 'Subscribed — check your inbox to confirm.' },
        ],
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════ BLOG DETAIL
  {
    pageKey: 't3_blogDetail',
    label: 'Blog detail',
    icon: Newspaper,
    intro:
      'These settings apply to every article page. The article’s own title, cover, author and '
      + 'body are edited under Blogs; the contents list is built from the article’s own headings.',
    sections: [
      {
        title: 'Author box',
        fields: [
          { key: 'authorBoxEnabled', label: 'Show author box', type: 'toggle', default: true },
        ],
      },
      {
        title: 'Sidebar CTA',
        fields: [
          { key: 'sidebarCtaTitle', label: 'Title', type: 'text', placeholder: 'Preparing for backend interviews?' },
          { key: 'sidebarCtaText', label: 'Text', type: 'textarea', rows: 2 },
          { key: 'sidebarCtaLink', label: 'Button', type: 'link', placeholder: 'See the programme', hrefPlaceholder: '/courses' },
        ],
      },
      {
        title: 'Related articles',
        fields: [
          { key: 'relatedTitle', label: 'Section heading', type: 'text', placeholder: 'Related reading' },
        ],
      },
      {
        title: 'Closing CTA',
        fields: closingCta({
          title: 'Reading about it is the easy half.',
          text: 'Sit in on a live class and see how these get taught.',
          primary: 'Book a Free Demo',
          secondary: 'Talk to a Counsellor',
        }),
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════ CONTACT
  {
    pageKey: 't3_contact',
    label: 'Contact',
    icon: Phone,
    sections: [
      {
        title: 'Page header',
        fields: [
          { key: 'crumb', label: 'Breadcrumb label', type: 'text', placeholder: 'Contact' },
          { key: 'kicker', label: 'Kicker', type: 'text', placeholder: 'Talk to us' },
          { key: 'title', label: 'Page title', type: 'text', placeholder: 'One conversation before you commit six months.' },
          { key: 'lead', label: 'Lead', type: 'textarea', rows: 3 },
        ],
      },
      {
        title: 'Callback form',
        fields: [
          { key: 'formTitle', label: 'Title', type: 'text', placeholder: 'Request a callback' },
          { key: 'formSub', label: 'Subtitle', type: 'textarea', rows: 2 },
          { key: 'formCta', label: 'Submit button', type: 'text', placeholder: 'Request a Callback' },
          { key: 'formNote', label: 'Footnote', type: 'text', placeholder: 'We reply within one working day.' },
          { key: 'formSuccess', label: 'Success message', type: 'textarea', rows: 2 },
        ],
      },
      {
        title: 'Contact channels',
        fields: [
          {
            key: 'channels',
            label: 'Channels',
            type: 'repeater',
            itemLabel: 'Channel',
            titleKey: 'title',
            addLabel: 'Add channel',
            max: 4,
            defaultItem: { icon: 'phone', title: '', value: '', href: '', note: '' },
            item: [
              icon(),
              { key: 'title', label: 'Title', type: 'text', placeholder: 'Call us' },
              { key: 'value', label: 'Value', type: 'text', placeholder: '+91 98765 43210' },
              { key: 'href', label: 'Link', type: 'text', placeholder: 'tel:+919876543210' },
              { key: 'note', label: 'Note', type: 'text', placeholder: 'Mon–Sat, 9 am – 8 pm' },
            ],
          },
        ],
      },
      {
        title: 'Campus hours',
        fields: [
          { key: 'hoursTitle', label: 'Title', type: 'text', placeholder: 'Campus hours' },
          { key: 'hoursNote', label: 'Note', type: 'text', placeholder: 'Mon–Sat' },
          {
            key: 'hoursRows',
            label: 'Rows',
            type: 'repeater',
            itemLabel: 'Row',
            titleKey: 'label',
            addLabel: 'Add row',
            max: 7,
            inline: true,
            defaultItem: { label: '', value: '' },
            item: [
              { key: 'label', label: 'Day(s)', type: 'text', placeholder: 'Monday – Friday' },
              { key: 'value', label: 'Hours', type: 'text', placeholder: '9 am – 8 pm' },
            ],
          },
        ],
      },
      {
        title: 'Visit the campus',
        fields: [
          { key: 'visitEnabled', label: 'Show this section', type: 'toggle', default: true },
          { key: 'visitKicker', label: 'Kicker', type: 'text', placeholder: 'Visit the campus' },
          { key: 'visitTitle', label: 'Title', type: 'text', placeholder: 'Sit in on a class before you decide.' },
          { key: 'visitLead', label: 'Lead', type: 'textarea', rows: 2 },
          { key: 'visitImage', label: 'Map or campus photo', type: 'image' },
          { key: 'visitImageAlt', label: 'Image — alt text (SEO)', type: 'text', placeholder: 'Map showing the campus location' },
          {
            key: 'visitCampuses',
            label: 'Campuses',
            type: 'repeater',
            itemLabel: 'Campus',
            titleKey: 'name',
            addLabel: 'Add campus',
            max: 6,
            defaultItem: { name: '', address: '', mapUrl: '' },
            item: [
              { key: 'name', label: 'Name', type: 'text', placeholder: 'Hyderabad — main campus' },
              { key: 'address', label: 'Address', type: 'textarea', rows: 2 },
              { key: 'mapUrl', label: 'Directions link', type: 'text', placeholder: 'https://maps.google.com/…' },
            ],
          },
          { key: 'visitPrimaryCta', label: 'Primary button', type: 'link', placeholder: 'Book a campus visit', hrefPlaceholder: '/contact' },
          { key: 'visitSecondaryCta', label: 'Secondary button', type: 'link', placeholder: 'Get directions' },
        ],
      },
      {
        title: 'FAQ',
        fields: [
          { key: 'faqKicker', label: 'Kicker', type: 'text', placeholder: 'Before you call' },
          { key: 'faqTitle', label: 'Title', type: 'text', placeholder: 'Quick answers to the usual questions.' },
          { key: 'faqLead', label: 'Lead', type: 'textarea', rows: 2 },
          { key: 'faqCta', label: 'Link', type: 'link', placeholder: 'Browse all programmes', hrefPlaceholder: '/courses' },
          faqItems(),
        ],
      },
      {
        title: 'Closing CTA',
        fields: closingCta({
          title: 'Prefer to just watch a class?',
          text: 'Book a free demo and sit in on a live session.',
          primary: 'Book a Free Demo',
          secondary: 'Explore Courses',
        }),
      },
    ],
  },

  // ════════════════════════════════════════════════════════════ PLACEMENTS
  {
    pageKey: 't3_placements',
    label: 'Placements',
    icon: Briefcase,
    intro:
      'This page is only published when “Placements page” is switched on under Global → Optional pages.',
    sections: [
      {
        title: 'Page header',
        fields: [
          { key: 'crumb', label: 'Breadcrumb label', type: 'text', placeholder: 'Placements' },
          { key: 'kicker', label: 'Kicker', type: 'text', placeholder: 'Career support' },
          { key: 'title', label: 'Page title', type: 'text', placeholder: 'We prepare you properly, then open the doors we have.' },
          { key: 'lead', label: 'Lead', type: 'textarea', rows: 3 },
          { key: 'primaryCta', label: 'Primary button', type: 'link', placeholder: 'Talk to a Counsellor', hrefPlaceholder: '/contact' },
          { key: 'secondaryCta', label: 'Secondary button', type: 'link', placeholder: 'Explore Courses', hrefPlaceholder: '/courses' },
          { key: 'heroImage', label: 'Hero image', type: 'image' },
          { key: 'heroImageAlt', label: 'Hero image — alt text (SEO)', type: 'text' },
          {
            key: 'outcomes',
            label: 'Outcome counters',
            type: 'repeater',
            itemLabel: 'Outcome',
            titleKey: 'label',
            addLabel: 'Add outcome',
            max: 4,
            inline: true,
            defaultItem: { num: '', label: '' },
            item: [
              { key: 'num', label: 'Value', type: 'text', placeholder: '500+' },
              { key: 'label', label: 'Label', type: 'text', placeholder: 'Career success stories' },
            ],
          },
        ],
      },
      {
        title: 'How it works',
        fields: [
          { key: 'processKicker', label: 'Kicker', type: 'text', placeholder: 'How it works' },
          { key: 'processTitle', label: 'Title', type: 'text', placeholder: 'Five stages, running alongside the syllabus.' },
          { key: 'processLead', label: 'Lead', type: 'textarea', rows: 2 },
          {
            key: 'processSteps',
            label: 'Steps',
            type: 'repeater',
            itemLabel: 'Step',
            titleKey: 'title',
            addLabel: 'Add step',
            max: 6,
            defaultItem: { n: '', title: '', text: '' },
            item: [
              { key: 'n', label: 'Number', type: 'text', placeholder: '01', maxLength: 2 },
              { key: 'title', label: 'Title', type: 'text', placeholder: 'Portfolio review' },
              { key: 'text', label: 'Text', type: 'textarea', rows: 2 },
            ],
          },
          { key: 'processDisclaimer', label: 'Disclaimer', type: 'textarea', rows: 3, placeholder: 'Placement support is assistance, not a guarantee.' },
        ],
      },
      {
        title: 'Hiring partners',
        fields: [
          {
            key: 'partnersEnabled',
            label: 'Show this section',
            type: 'toggle',
            default: false,
            hint: 'Only display logos you have written permission to use.',
          },
          { key: 'partnersTitle', label: 'Title', type: 'text', placeholder: 'Where our learners work' },
          {
            key: 'partnersLogos',
            label: 'Partners',
            type: 'repeater',
            itemLabel: 'Partner',
            titleKey: 'name',
            addLabel: 'Add partner',
            max: 24,
            defaultItem: { name: '', logo: '' },
            coerceRow: (row) => (typeof row === 'string' ? { name: row, logo: '' } : row),
            item: [
              { key: 'name', label: 'Company name', type: 'text' },
              { key: 'logo', label: 'Logo', type: 'image' },
            ],
          },
        ],
      },
      {
        title: 'Success stories',
        fields: [
          { key: 'storiesKicker', label: 'Kicker', type: 'text', placeholder: 'Success stories' },
          { key: 'storiesTitle', label: 'Title', type: 'text', placeholder: 'Career changes, told plainly.' },
          { key: 'storiesLead', label: 'Lead', type: 'textarea', rows: 2 },
          {
            key: 'storiesSource',
            label: 'Where the stories come from',
            type: 'select',
            options: [
              { value: 'testimonials', label: 'My published testimonials' },
              { value: 'manual', label: 'Written below' },
            ],
            emptyOption: 'My published testimonials',
          },
          {
            key: 'storiesItems',
            label: 'Stories',
            type: 'repeater',
            hint: 'Only used when “Written below” is selected.',
            itemLabel: 'Story',
            titleKey: 'name',
            addLabel: 'Add story',
            max: 9,
            defaultItem: { from: '', to: '', quote: '', name: '', role: '' },
            item: [
              { key: 'from', label: 'Career from', type: 'text', placeholder: 'Manual testing' },
              { key: 'to', label: 'Career to', type: 'text', placeholder: 'Software Engineer' },
              { key: 'quote', label: 'Quote', type: 'textarea', rows: 3 },
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'role', label: 'Programme · batch', type: 'text', placeholder: 'Java Full Stack · 2025 batch' },
            ],
          },
        ],
      },
      {
        title: 'Recent placements table',
        fields: [
          {
            key: 'recentEnabled',
            label: 'Show this section',
            type: 'toggle',
            default: false,
            hint: 'Publish real, consented placements only — never sample rows.',
          },
          { key: 'recentKicker', label: 'Kicker', type: 'text', placeholder: 'Recent placements' },
          { key: 'recentTitle', label: 'Title', type: 'text', placeholder: 'A sample from the last two cohorts.' },
          { key: 'recentNote', label: 'Note', type: 'text', placeholder: 'Company names shown with each learner’s consent' },
          {
            key: 'recentRows',
            label: 'Rows',
            type: 'repeater',
            itemLabel: 'Placement',
            titleKey: 'learner',
            addLabel: 'Add placement',
            max: 20,
            inline: true,
            defaultItem: { learner: '', role: '', programme: '' },
            item: [
              { key: 'learner', label: 'Learner', type: 'text', placeholder: 'Priya S.' },
              { key: 'role', label: 'Role', type: 'text', placeholder: 'Software Engineer' },
              { key: 'programme', label: 'Programme', type: 'text', placeholder: 'Java Full Stack' },
            ],
          },
        ],
      },
      {
        title: 'FAQ',
        fields: [
          { key: 'faqKicker', label: 'Kicker', type: 'text', placeholder: 'Placement FAQs' },
          { key: 'faqTitle', label: 'Title', type: 'text', placeholder: 'The questions worth asking any institute.' },
          { key: 'faqLead', label: 'Lead', type: 'textarea', rows: 2 },
          faqItems(),
        ],
      },
      {
        title: 'Closing CTA',
        fields: closingCta({
          title: 'Ask us about outcomes from your batch.',
          text: 'A counsellor will tell you what the last two cohorts actually did.',
          primary: 'Talk to a Counsellor',
          secondary: 'Request a Callback',
        }),
      },
    ],
  },
];

export default THEME3_SCHEMA;
